// @ts-check
import imageCompression from 'browser-image-compression';
import { checkWhatsAppMedia } from './whatsapp';

const MB = 1024 * 1024;
/** What the picker accepts before we compress (Meta's own caps are lower for image/video; see utils/whatsapp.js). */
export const RAW_CAPS = Object.freeze({ image: 30 * MB, video: 100 * MB, audio: 16 * MB, document: 25 * MB });
const IMAGE_META_CAP = 5 * MB;
const VIDEO_META_CAP = 16 * MB;
/** Photo formats Meta rejects but we can turn into JPEG in the browser. */
const CONVERT_EXTS = new Set(['webp', 'heic', 'heif']);
const extOf = (name) => (String(name).includes('.') ? String(name).split('.').pop().toLowerCase() : '');
const jpgName = (name) => String(name).replace(/\.[^.]+$/, '') + '.jpg';

/** HEIC/HEIF -> JPEG blob. The decoder is large, so it only loads when someone actually picks one. */
const heicToJpeg = async (file) => {
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  return Array.isArray(out) ? out[0] : out;
};

/**
 * Checks a picked file against the raw caps and, for photos over Meta's 5 MB, compresses it in the
 * browser. Videos over 16 MB are allowed and flagged so the server compresses them on upload.
 * @param {File} file
 * @returns {Promise<{ok: true, file: File, serverCompress: boolean} | {ok: false, message: string}>}
 */
export const prepareForWhatsApp = async (file) => {
  const convert = CONVERT_EXTS.has(extOf(file.name));
  const known = checkWhatsAppMedia(file.name, 0);
  const kind = convert ? 'image' : known.ok ? known.kind : null;
  if (!kind) return { ok: false, message: 'WhatsApp does not accept this file type.' };
  const cap = RAW_CAPS[kind];
  if (file.size > cap) return { ok: false, message: `This ${kind} is over the ${cap / MB} MB limit.` };

  if (kind === 'image' && (convert || file.size > IMAGE_META_CAP)) {
    try {
      // WebP decodes natively; HEIC needs the decoder first. Both come out as JPEG, then get the same size squeeze.
      const source =
        extOf(file.name) === 'webp'
          ? file
          : convert
            ? new File([await heicToJpeg(file)], jpgName(file.name), { type: 'image/jpeg' })
            : file;
      if (convert && source.size <= IMAGE_META_CAP && extOf(file.name) !== 'webp') {
        return { ok: true, file: source, serverCompress: false };
      }
      const blob = await imageCompression(source, {
        useWebWorker: true,
        fileType: 'image/jpeg',
        maxSizeMB: 4.5,
        maxWidthOrHeight: 4096,
      });
      if (blob.size > IMAGE_META_CAP) {
        return { ok: false, message: 'This photo is still over 5 MB after compressing. Try a smaller photo.' };
      }
      return { ok: true, file: new File([blob], jpgName(file.name), { type: 'image/jpeg' }), serverCompress: false };
    } catch {
      return {
        ok: false,
        message: convert
          ? "Couldn't convert this photo to JPEG. Try exporting it as a JPEG or PNG."
          : 'Could not compress this photo. Try a smaller one.',
      };
    }
  }
  return { ok: true, file, serverCompress: kind === 'video' && file.size > VIDEO_META_CAP };
};
